import React from "react";
import { ChallengeTableInspectionsProgramsVersionsItem } from "../../../api/types/dtos/challenge/ChallengeDto";
import {
  ChallengeCellContext,
  CellStateValue,
} from "./ChallengesDetailedTable.cellContext";
import LoadingSpinner from "../../../components/LoadingSpinner";
import MultiStateButton from "../../../components/MultiStateButton";
import IconButton from "@mui/material/IconButton";
import EditIcon from "@mui/icons-material/Edit";
import styles from "../ChallengesDetailedTable.module.scss";
import { isEndDatePreviousYear } from "../challengeUtils";

interface ChallengeCellProps {
  item: ChallengeTableInspectionsProgramsVersionsItem;
  rowId: string;
  pviId: string;
  cpNumber: string;
  measurementTemplate?: string;
}

const ChallengeCell: React.FC<ChallengeCellProps> = React.memo(
  ({ item, rowId, pviId, cpNumber, measurementTemplate }) => {
    const context = React.useContext(ChallengeCellContext);
    if (!context) return null;

    const { cellState, updating, handleCellStateChange, handleOpenCellModal } =
      context;
    const cellKey = `${rowId}_${pviId}`;
    const local = cellState[cellKey] as CellStateValue | undefined;
    const updateKey = `${cellKey}_${item.id}`;
    const isLoading = updating[updateKey];

    const currentIsActive = local?.isActive ?? item.isActive;
    const currentIsApproved = local?.isApproved ?? item.isApproved;
    const currentEndDate = local?.endDate ?? item.endDate;

    const isApprovedButEmptyValue =
      currentIsActive &&
      currentIsApproved &&
      isEndDatePreviousYear(currentEndDate);

    return (
      <div className={styles.cellContentWrapper}>
        {isLoading ? (
          <LoadingSpinner size="small" />
        ) : (
          <MultiStateButton
            isActive={currentIsActive}
            isApproved={currentIsApproved}
            isApprovedButEmptyValue={isApprovedButEmptyValue}
            onChange={(next) => handleCellStateChange(cellKey, item.id, next)}
          />
        )}
        <IconButton
          size="small"
          onClick={() => {
            const currentItemState: ChallengeTableInspectionsProgramsVersionsItem =
              {
                ...item,
                ...(local?.comments !== undefined && {
                  comments: local.comments,
                }),
                ...(local?.value !== undefined && { value: local.value }),
                isActive: currentIsActive,
                isApproved: currentIsApproved,
              };
            handleOpenCellModal(currentItemState, cellKey, measurementTemplate);
          }}
          aria-label={`Editar item ${cpNumber}`}
        >
          <EditIcon fontSize="small" />
        </IconButton>
      </div>
    );
  },
);

ChallengeCell.displayName = 'ChallengeCell';

export default ChallengeCell;
